/*
 * Copyright © 2022 EPO: European Patent Office.
 * ALL RIGHTS RESERVED
 */

import {
  lookupItemI18nProperties,
  lookupItemNameI18nProperties,
  lookupItemProperties,
  lookupItemShortDateProperties,
  lookupItemFloatProperties,
  lookupItemBooleanProperties,
  lookupitemExtraLanguagesProperties,
  lookupMiscellaneousItemsForAggregation,
  lookupContextProperties,
  lookupContextAttachmentProperties,
  lookupContextFacsimileSignatureAttachmentProperties,
  lookupContextLegacyPartyProperties,
  lookupCapitalizeWords,
} from '../utils/objectUtils';

const caseKeywords = Object.freeze({
  ALLOWED_VALUES: 'allowedValues',
  NOT: 'not',
  SKIP_EMPTY_REAL_VALUE: 'skipEmptyRealValue',
  ANY: '*',
});

/**
 * Checks if the conditional value is defined to allow any value (*) or a single value
 */
const isMatchingPrimitiveValue = (caseValue, entityValue, context) => {
  const value = lookupContextProperties(caseValue, context);
  return value === caseKeywords.ANY || value === entityValue;
};

/**
 * Checks if a value is defined in an array of allowed values
 */
const isMatchingArrayValue = (caseArrayValue, entityValue, context) => {
  const value = lookupContextProperties(caseArrayValue, context);
  return !!value.find(item => item === entityValue);
};

/**
 * Checks a case value matches the entity value.
 * @param caseValue case property value
 * @param entityValue data value for the case key
 * @returns {boolean} true if the entity value matches the case condition
 */
function isMatchingEntityValue(caseValue, entityValue, context) {
  switch (typeof caseValue) {
    case 'object':
      // E.g.: ["APPLICANT_LEGAL_ENTITY", "APPLICANT_NATURAL_PERSON"]
      if (Array.isArray(caseValue)) {
        return isMatchingArrayValue(caseValue, entityValue, context);
      }
      // E.g.: { "allowedValues": ['a', 'b', 'c'] }
      // E.g.: { "allowedValues": "" }
      if (caseValue?.hasOwnProperty(caseKeywords.ALLOWED_VALUES)) {
        return Array.isArray(caseValue[caseKeywords.ALLOWED_VALUES])
          ? isMatchingArrayValue(caseValue[caseKeywords.ALLOWED_VALUES], entityValue, context)
          : isMatchingPrimitiveValue(caseValue[caseKeywords.ALLOWED_VALUES], entityValue, context);
      }
      // E.g.: { "not": [ "APPLICANT_LEGAL_ENTITY", "APPLICANT_NATURAL_PERSON" ] }
      // E.g.: { "not": "APPLICANT_LEGAL_ENTITY" }
      if (caseValue?.hasOwnProperty(caseKeywords.NOT)) {
        return Array.isArray(caseValue[caseKeywords.NOT])
          ? !isMatchingArrayValue(caseValue[caseKeywords.NOT], entityValue, context)
          : !isMatchingPrimitiveValue(caseValue[caseKeywords.NOT], entityValue, context);
      }
      return true; // Returns true as there are no other values defined/to compare against the real value

    case 'function':
      return false;

    default:
      return isMatchingPrimitiveValue(caseValue, entityValue);
  }
}

/**
 * Checks if an entry/title complies with the conditions defined in the uiSchema 'case' property.
 * @param {Object} configEntry object containing the condition (case statement) for the entry/title
 * @param {Object} entity an object hosting the real values of the entity
 * @returns true if the entry/title complies with conditions defined in the uiSchema. Otherwise, it returns false.
 *
 * Example of all options available in a case statement defined for an entry or title card
 *     Match values where role = "APPLICANT_LEGAL_ENTITY"
 *     "role": "APPLICANT_LEGAL_ENTITY"
 *
 *     Match any value for role
 *     "role": "*"
 *
 *     Match values where role = "APPLICANT_LEGAL_ENTITY" or "APPLICANT_NATURAL_PERSON"
 *     "role": ["APPLICANT_LEGAL_ENTITY", "APPLICANT_NATURAL_PERSON"]
 *
 *     Match values where role is not equal to "APPLICANT_LEGAL_ENTITY"
 *     "role": {
 *       "not": "APPLICANT_LEGAL_ENTITY"
 *     }
 *
 *     Match values where role is not equal to "APPLICANT_LEGAL_ENTITY" or "APPLICANT_NATURAL_PERSON"
 *     "role": {
 *       "not": ["APPLICANT_LEGAL_ENTITY", "APPLICANT_NATURAL_PERSON"]
 *     }
 *
 *     Skip check when real value is empty/undefined. i.e. showing regardless of the value recorded (even if empty)
 *     "role": {
 *       "skipEmptyRealValue": true
 *     }
 *
 *     Can also define a list of allowed values if the property is defined as an object
 *     "role": {
 *       "allowedValues": "",
 *       "skipEmptyRealValue": true
 *     }
 */
export const isMatchingConfigEntry = (configEntry, entity, context) => {
  if (!configEntry.case) return true;

  // checks all the case conditions
  return Object.entries(configEntry.case).every(([caseKey, caseValue]) => {
    const entityValue = caseKey.split('.').reduce((acum, n) => acum?.[n] ?? undefined, entity);

    // the configuration entry is not valid if the entity value is empty or the 'skipEmptyRealValue flag is not set
    // TODO: review configuration uses of SKIP_EMPTY_REAL_VALUE as it looks redundant
    if (!entityValue && !caseValue?.[caseKeywords.SKIP_EMPTY_REAL_VALUE]) return false;

    const safeEntityValue = entityValue || '';
    return isMatchingEntityValue(caseValue, safeEntityValue, context);
  });
};

/**
 * Map entity fields according to the given configuration (icon, title, key / value entries).
 * @param {*} config uischema ui:summaryItem configuration that contains icon, titles, entries, defaultValue
 * @param {*} entity current data entity item (array element)
 * @param {*} jsonschema submission jsonschema
 * @param {*} context context (usually RJSForm context)
 * @returns
 */
export function mapEntityFields(config, entity, jsonschema, context) {
  // TO-DO: Use case when no ui:summaryItem / entries / titles

  // Filtering entries/titles which comply with conditions
  let entries = config?.entries?.filter(entry => isMatchingConfigEntry(entry, entity));
  let cardTitle = config?.titles?.find(title => isMatchingConfigEntry(title, entity));
  let actions = config?.actions?.filter(action => isMatchingConfigEntry(action, entity, context));
  const defaultValue = config?.defaultValue || '';

  // TO-DO: Try to chain them
  // Lookup for item/i18n values.
  if (entries) {
    entries = lookupItemProperties(entries, entity, defaultValue);
    entries = lookupItemFloatProperties(entries, entity, defaultValue);
    entries = lookupItemBooleanProperties(entries, entity);
    entries = lookupItemI18nProperties(entries, entity, defaultValue);
    entries = lookupitemExtraLanguagesProperties(entries, entity, defaultValue);
    entries = lookupMiscellaneousItemsForAggregation(entries);

    // Validation schema might be optional for some procedures
    if (jsonschema) {
      entries = lookupItemNameI18nProperties(entries, entity, jsonschema, defaultValue);
    }
    entries = lookupItemShortDateProperties(entries, entity, defaultValue);
    entries = lookupContextAttachmentProperties(entries, entity, context);
    entries = lookupContextFacsimileSignatureAttachmentProperties(entries, entity, context);
    entries = lookupContextLegacyPartyProperties(entries, entity, context);
    entries = lookupCapitalizeWords(entries);
  }
  if (cardTitle) {
    [cardTitle] = lookupItemProperties([cardTitle], entity, defaultValue);
    [cardTitle] = lookupItemFloatProperties([cardTitle], entity, defaultValue);
    [cardTitle] = lookupItemBooleanProperties([cardTitle], entity);
    [cardTitle] = lookupItemI18nProperties([cardTitle], entity, defaultValue);

    // Validation schema might be optional for some procedures
    if (jsonschema) {
      [cardTitle] = lookupItemNameI18nProperties([cardTitle], entity, jsonschema, defaultValue);
    }
    [cardTitle] = lookupItemShortDateProperties([cardTitle], entity, defaultValue);
  }

  if (actions) {
    actions = lookupContextProperties(actions, context, defaultValue);
    actions = lookupItemProperties(actions, entity, defaultValue);
  }

  return {
    entries,
    cardTitle: cardTitle?.value,
    icon: cardTitle?.icon || config?.icon,
    actions,
  };
}
